import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const WS_BASE = API_URL.replace(/^http/, 'ws') + '/ws/chat';

export default function useWebSocket(matchId, userId, onMessage) {
  const [connected, setConnected] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const userIdRef = useRef(userId);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  const sendMessage = useCallback((text) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', text }));
      return true;
    }
    return false;
  }, []);

  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('ahoow_token');
    if (!token || !matchId) return;

    let reconnectTimer;
    const connect = () => {
      const ws = new WebSocket(`${WS_BASE}/${matchId}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'typing' && data.user_id !== userIdRef.current) {
            setOtherTyping(true);
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 3000);
          } else {
            onMessageRef.current?.(data);
          }
        } catch (e) { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        setConnected(false);
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      clearTimeout(typingTimeoutRef.current);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [matchId]);

  return { connected, otherTyping, sendMessage, sendTyping };
}
